import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Power, Pause, ChevronRight, TreePine, Wind, Sparkles, Volume2, VolumeX } from 'lucide-react';
import useLanguage from '@/hooks/useLanguage';
import { useAuth } from '@/contexts/AuthContext';
import NIGHT_CATEGORIES from '@/config/nightTracks';
import { audioSessionManager } from '@/services/audioSessionManager';

// Cocoon icon — soft diffuse halo circle (replaces Moon)
const CocoonIcon = ({ size = 16, className = '', animate = false }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    style={animate ? {
      animation: 'cocoonHaloBreathe 7s ease-in-out infinite',
    } : undefined}
  >
    <circle cx="12" cy="12" r="8" fill="currentColor" opacity="0.12" />
    <circle cx="12" cy="12" r="5.5" fill="currentColor" opacity="0.25" />
    <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.5" />
  </svg>
);

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://hearen-backend.onrender.com';
const NIGHT_SESSION_KEY = 'hearen_night_sessions';
const NIGHT_CATEGORY_KEY = 'hearen_night_category';
const UPSELL_THRESHOLD = 3;

const ALL_TRACKS = NIGHT_CATEGORIES.flatMap(c => c.tracks);
const FREE_TRACKS = ALL_TRACKS.filter(t => t.free);
const PREMIUM_CATEGORIES = NIGHT_CATEGORIES.filter(c => !c.free);

const pickNextTrack = (pool, history) => {
  if (pool.length <= 1) return pool[0];
  // Exclude recently played tracks (keep at least 1 option)
  const maxExclude = Math.min(history.length, pool.length - 1);
  const recentIds = history.slice(-maxExclude);
  const available = pool.filter(t => !recentIds.includes(t.id));
  const pick = available.length > 0 ? available : pool;
  return pick[Math.floor(Math.random() * pick.length)];
};

const getSessionCount = () => {
  try { return parseInt(localStorage.getItem(NIGHT_SESSION_KEY) || '0', 10); }
  catch { return 0; }
};
const incrementSession = () => {
  try { localStorage.setItem(NIGHT_SESSION_KEY, String(getSessionCount() + 1)); }
  catch { /* silent */ }
};

// ─── PREMIUM DISCOVERY OVERLAY ─────────────────────────────────
const DiscoverOverlay = ({ isFr, onClose }) => {
  const navigate = useNavigate();
  const catIcons = { nature: TreePine, atmospheres: Sparkles };

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'rgba(3,0,20,0.97)' }}>
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
        <CocoonIcon size={40} className="text-indigo-400/70 mb-6" />
        <h2 className="text-xl font-semibold text-white/80 mb-2">
          {isFr ? 'D\'autres espaces t\'attendent' : 'Other spaces await'}
        </h2>
        <p className="text-white/40 text-sm leading-relaxed max-w-[280px] mb-10">
          {isFr
            ? 'Des sons de nature, des atmosphères profondes… pour les moments qui ont besoin d\'autre chose.'
            : 'Nature sounds, deep atmospheres… for the moments that need something else.'}
        </p>

        {/* Premium categories preview */}
        <div className="w-full max-w-[300px] space-y-3 mb-10">
          {PREMIUM_CATEGORIES.map(cat => {
            const Icon = catIcons[cat.id] || Wind;
            return (
              <div
                key={cat.id}
                className="flex items-center gap-3 px-4 py-3 rounded-xl"
                style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.1)' }}
              >
                <Icon className="w-5 h-5 text-indigo-400/60 flex-shrink-0" />
                <div className="text-left">
                  <p className="text-white/60 text-sm font-medium">{isFr ? cat.labelFr : cat.labelEn}</p>
                  <p className="text-white/25 text-xs">
                    {cat.tracks.map(t => isFr ? t.labelFr : t.labelEn).join(', ')}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <button
          onClick={() => navigate('/premium')}
          data-testid="night-discover-cta"
          className="px-8 py-3 rounded-full transition-all duration-300 active:scale-95 mb-4"
          style={{
            background: 'linear-gradient(135deg, rgba(79,70,229,0.5), rgba(99,102,241,0.35))',
            border: '1px solid rgba(129,140,248,0.3)',
          }}
        >
          <span className="text-indigo-100 font-medium text-sm">
            {isFr ? 'Découvrir' : 'Discover'}
          </span>
        </button>

        <button
          onClick={onClose}
          className="text-white/25 text-xs"
          data-testid="night-discover-close"
        >
          {isFr ? 'Pas maintenant' : 'Not now'}
        </button>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ────────────────────────────────────────────
const NightMode = () => {
  const [isEnteringCocoon, setIsEnteringCocoon] = useState(false);
  const navigate = useNavigate();
  const { t, lang: currentLang } = useLanguage();
  const { user } = useAuth();
  const isFr = currentLang === 'fr';
  const isPremium = user?.isPremium || user?.subscription === 'premium' || user?.role === 'admin';

  // Category selection (premium only, persisted)
  const [selectedCategory, setSelectedCategory] = useState(() => {
    if (!isPremium) return 'appliances';
    return localStorage.getItem(NIGHT_CATEGORY_KEY) || 'appliances';
  });

  const handleCategoryChange = useCallback((catId) => {
    setSelectedCategory(catId);
    localStorage.setItem(NIGHT_CATEGORY_KEY, catId);
    playedHistoryRef.current = []; // Reset anti-repeat for new category
  }, []);

  // Track pool: filtered by category for premium, free tracks only for free users
  const trackPool = useMemo(() => {
    if (!isPremium) return FREE_TRACKS;
    const cat = NIGHT_CATEGORIES.find(c => c.id === selectedCategory);
    return cat ? cat.tracks : FREE_TRACKS;
  }, [isPremium, selectedCategory]);

  const [phase, setPhase] = useState('entry');
  const [isPlaying, setIsPlaying] = useState(false);
  const volume = 70; // Fixed at 70%
  const [trackLabel, setTrackLabel] = useState('');
  const [fadeIn, setFadeIn] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [upsellMsg, setUpsellMsg] = useState(null);
  const [showDiscover, setShowDiscover] = useState(false);

  // V1.1.2: Voice toggle — synced with localStorage (same key as PreSessionSheet)
  const [voiceOn, setVoiceOn] = useState(() => {
    return localStorage.getItem('hearen_voice_enabled') !== 'false';
  });
  const voiceOnRef = useRef(voiceOn);

  const audioRef = useRef(null);          // Single <audio> element for EVERYTHING
  const currentTrackIdRef = useRef(null);
  const nightVoicePlayedRef = useRef(false);
  const voiceActiveRef = useRef(false);   // True while voice message is playing (guards watchdog)
  const bgWarmupRef = useRef(null);
  const lastTapRef = useRef(0);
  const preloadDoneRef = useRef(false);
  const nextCountRef = useRef(0);
  const nextPreloadRef = useRef(null);
  const voiceUrlRef = useRef(null);
  const bgTrackUrlRef = useRef(null);
  const userPausedRef = useRef(false);
  const watchdogRef = useRef(null);
  const playedHistoryRef = useRef([]);
  const autoAdvanceRef = useRef(null);
  const autoAdvancingRef = useRef(false);
  const nextPreparedTrackRef = useRef(null);
  const trackBlobCacheRef = useRef({});

  // No-op debug (production)
  const addDebug = useCallback(() => {}, []);

  // ─── iOS blob helper: download track to blob URL (bypasses missing HTTP 206) ───
  const getTrackBlobUrl = useCallback(async (trackId, trackUrl) => {
    if (trackBlobCacheRef.current[trackId]) return trackBlobCacheRef.current[trackId];
    try {
      console.log(`[Cocoon] Downloading blob: ${trackId}`);
      const res = await fetch(trackUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      trackBlobCacheRef.current[trackId] = blobUrl;
      console.log(`[Cocoon] Blob ready: ${trackId} (${(blob.size/1024/1024).toFixed(1)}MB)`);
      return blobUrl;
    } catch (e) {
      console.warn(`[Cocoon] Blob download failed: ${trackId}: ${e.message}`);
      return null;
    }
  }, []);

  // ─── Preload first track as blob on mount (entry screen time) ───
  useEffect(() => {
    if (trackPool.length === 0) return;
    const first = trackPool[0];
    const url = `${API_BASE_URL}/api/r2/stream/${first.r2cat}/${first.file}`;
    getTrackBlobUrl(first.id, url);
  }, [trackPool, getTrackBlobUrl]);

  // V1.1.2: Keep voiceOnRef in sync
  useEffect(() => {
    voiceOnRef.current = voiceOn;
    localStorage.setItem('hearen_voice_enabled', voiceOn ? 'true' : 'false');
  }, [voiceOn]);

  // ─── iOS Safari safety net for BG audio ───
  // No loop — auto-advance to next track when current ends.
  const installBgSafeguards = useCallback((audio) => {
    audio.onended = () => {
      if (autoAdvanceRef.current) {
        autoAdvanceRef.current(audio);
      }
    };
  }, []);

  // Keep auto-advance function up-to-date (avoids stale closures in onended)
  useEffect(() => {
    autoAdvanceRef.current = (audio) => {
      if (autoAdvancingRef.current) {
        console.warn('[Cocoon] Auto-advance already running — ignored');
        return;
      }

      autoAdvancingRef.current = true;

    const next = nextPreparedTrackRef.current || pickNextTrack(trackPool, playedHistoryRef.current);
    nextPreparedTrackRef.current = null;
    playedHistoryRef.current = [...playedHistoryRef.current.slice(-(trackPool.length)), next.id];
    currentTrackIdRef.current = next.id;

    const label = isFr ? next.labelFr : next.labelEn;
    setTrackLabel(label);

    const directUrl = `${API_BASE_URL}/api/r2/stream/${next.r2cat}/${next.file}`;
    const blobUrl = trackBlobCacheRef.current[next.id];

    if (!blobUrl) {
      console.warn(`[Cocoon] Next blob not ready, cannot auto-advance safely: ${next.id}`);

      // Important: prepare it now, but DO NOT try to play it too late.
      getTrackBlobUrl(next.id, directUrl);

      autoAdvancingRef.current = false;
      return;
    }

    console.log(`[Cocoon] Auto-advance with READY blob: ${next.id}`);

    audio.pause();
    audio.removeAttribute('src');
    audio.load();

    audio.loop = false;
    audio.preload = 'auto';
    audio.src = blobUrl;

    userPausedRef.current = false;

    audio.load();

    setTimeout(() => {
      audio.play()
        .then(() => {
          console.log(`[Cocoon] ✅ Auto-advance blob play OK: ${next.id}`);
          autoAdvancingRef.current = false;
        })
        .catch(e => {
          console.error(`[Cocoon] ❌ Auto-advance blob play KO: ${e.name}: ${e.message}`);
          autoAdvancingRef.current = false;
        });
    }, 150);

    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: label,
        artist: 'Hearen',
        album: 'Cocoon',
      });
    }
  };
}, [trackPool, isFr, getTrackBlobUrl]);
  // ─── Pre-fetch voice: call trace endpoint then resolve audio URL ───
  useEffect(() => {
    if (preloadDoneRef.current) return;
    preloadDoneRef.current = true;

    (async () => {
      try {
        const userId = user?.id || user?.user_id || 'anonymous';
        console.log(`[Cocoon][Voice] Preload start — API_BASE_URL="${API_BASE_URL}", lang="${currentLang}", userId="${userId}"`);

        // Step 1: night-debug trace endpoint
        const traceUrl = `${API_BASE_URL}/api/voice-messages/night-debug?lang=${currentLang}`;
        console.log(`[Cocoon][Voice] Step1 FINAL URL = ${traceUrl}`);
        const traceRes = await fetch(traceUrl);
        console.log(`[Cocoon][Voice] Step1 response: HTTP ${traceRes.status}`);
        if (traceRes.ok) {
          const t = await traceRes.json();
          const msgId = t.step3_message?.id;
          const verdict = Array.isArray(t.verdict) ? t.verdict[0] : (t.verdict || 'NO_DATA');
          console.log(`[Cocoon][Voice] Step1 verdict="${verdict}", msgId=${msgId || 'NONE'}`);
          if (msgId) {
            const voiceUrl = `${API_BASE_URL}/api/radio/voice-audio/${msgId}?stream=true&_v=${Date.now()}`;
            voiceUrlRef.current = voiceUrl;
            console.log(`[Cocoon][Voice] ✅ FINAL URL = ${voiceUrl}`);
            return;
          }
        }

        // Step 2: Fallback to /pick endpoint
        const pickUrl = `${API_BASE_URL}/api/voice-messages/pick?trigger_mode=night_start&theme=generic&lang=${currentLang}&voice=hera&user_id=${userId}`;
        console.log(`[Cocoon][Voice] Step2 FINAL URL = ${pickUrl}`);
        const res = await fetch(pickUrl);
        console.log(`[Cocoon][Voice] Step2 response: HTTP ${res.status}`);
        if (res.status === 204 || !res.ok) {
          console.warn('[Cocoon][Voice] Step2: no voice message found');
          return;
        }
        const msg = await res.json();
        console.log(`[Cocoon][Voice] Step2 msg: id=${msg?.id}, file_url=${msg?.file_url || 'NONE'}`);
        if (msg?.file_url) {
          const voiceUrl = `${API_BASE_URL}${msg.file_url}?stream=true&_v=${Date.now()}`;
          voiceUrlRef.current = voiceUrl;
          console.log(`[Cocoon][Voice] ✅ FINAL URL = ${voiceUrl}`);
        } else {
          console.warn('[Cocoon][Voice] Step2: /pick returned no file_url');
        }
      } catch (e) {
        console.error(`[Cocoon][Voice] ❌ Preload ERROR: ${e.name}: ${e.message}`);
        console.error(`[Cocoon][Voice] API_BASE_URL at error time: "${API_BASE_URL}"`);
      }
    })();

    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
      if (nextPreloadRef.current?.audio) { nextPreloadRef.current.audio.src = ''; }
      audioSessionManager.setPlaying('night', false, 'unmount');
      audioSessionManager.releaseSession('night');
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase === 'playing') {
      const tm = setTimeout(() => setFadeIn(true), 50);
      return () => clearTimeout(tm);
    }
  }, [phase]);

  // ─── Watchdog + visibility change: resume audio if iOS suspends it ───
  useEffect(() => {
    if (phase !== 'playing') return;

    // iOS Safari suspends audio on lock/background. Resume on return.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && audioRef.current && !userPausedRef.current && !voiceActiveRef.current) {
        const a = audioRef.current;
        if (a.ended && a.src && autoAdvanceRef.current) {
          // onended didn't fire in background → auto-advance now
          autoAdvanceRef.current(a);
        } else if (a.paused && a.src && !a.ended) {
          a.play().catch(() => {});
        }
      }
    };

    // Watchdog: every 10s, detect unexpected stop
    const id = setInterval(() => {
      // NEVER interfere during voice message playback
      if (!audioRef.current || userPausedRef.current || voiceActiveRef.current) return;
      const a = audioRef.current;
      if (a.ended && a.src && autoAdvanceRef.current) {
        autoAdvanceRef.current(a);
      } else if (a.paused && a.src && !a.ended) {
        a.play().catch(() => {});
      }
    }, 10000);
    watchdogRef.current = id;

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(id);
      watchdogRef.current = null;
    };
  }, [phase]);

  // Preload the NEXT track in background during current playback
  const preloadNextTrack = useCallback(() => {
    const next = pickNextTrack(trackPool, playedHistoryRef.current);
    const url = `${API_BASE_URL}/api/r2/stream/${next.r2cat}/${next.file}`;
    // Clean up previous preload
    if (nextPreloadRef.current?.audio) {
      nextPreloadRef.current.audio.src = '';
    }
    const preAudio = new Audio();
    preAudio.preload = 'auto';
    preAudio.src = url;
    preAudio.load();
    nextPreloadRef.current = { track: next, audio: preAudio };
  }, [trackPool]);

  // Show upsell message (subtle, fades away)
  const maybeShowUpsell = useCallback(() => {
    if (isPremium) return;
    nextCountRef.current += 1;
    const sessions = getSessionCount();
    const msgs = isFr
      ? ['Certaines nuits appellent autre chose…', 'Tu peux aussi explorer d\'autres ambiances…', 'La nature et l\'espace t\'attendent…']
      : ['Some nights call for something else…', 'You can also explore other ambiances…', 'Nature and space await you…'];
    if (nextCountRef.current >= 2 || sessions >= UPSELL_THRESHOLD) {
      const msg = msgs[nextCountRef.current % msgs.length];
      setUpsellMsg(msg);
      if (nextCountRef.current >= 4) {
        setTimeout(() => setShowDiscover(true), 2000);
      }
    }
  }, [isPremium, isFr]);

  // ─── ENTER: Single <audio> element, sequential (voice first → then BG track) ───
  // iOS Safari rule: play() must be called synchronously in user gesture. NO await before play().
  const handleEnter = useCallback(async () => {

  setIsEnteringCocoon(true);

  console.log('[Cocoon] ════════════════════════════════════════');
  console.log('[Cocoon] ▶ handleEnter CALLED at', new Date().toISOString());
  console.log('[Cocoon] phase before:', phase);

  // ── iOS Audio Session: acquire + unlock (MUST be in user gesture) ──
  console.log('[Cocoon] → BEFORE acquireSession("night")');

  // ── TEMP TEST Cocoon: bypass AudioSessionManager ──
  console.log('[Cocoon] TEMP: skipping acquireSession("night") and unlockAudioOnce("night")');

// try {
//   await audioSessionManager.acquireSession('night');
//   console.log('[Cocoon] ✅ acquireSession("night") OK, owner:', audioSessionManager.getCurrentOwner());
// } catch (e) {
//   console.error('[Cocoon] ❌ acquireSession FAILED:', e.name, e.message);
// }

// try {
//   const unlocked = await audioSessionManager.unlockAudioOnce('night');
//   console.log('[Cocoon] ✅ unlockAudioOnce result:', unlocked, ', isUnlocked:', audioSessionManager.isAudioUnlocked());
// } catch (e) {
//   console.error('[Cocoon] ❌ unlockAudioOnce FAILED:', e.name, e.message);
// }

    incrementSession();

    const track = pickNextTrack(trackPool, playedHistoryRef.current);
    playedHistoryRef.current = [track.id];
    const trackUrl = `${API_BASE_URL}/api/r2/stream/${track.r2cat}/${track.file}`;
    bgTrackUrlRef.current = trackUrl;
    let trackBlobUrl = trackBlobCacheRef.current[track.id] || null;

    console.log(`[Cocoon] Track selected: id=${track.id}, r2cat=${track.r2cat}, file=${track.file}`);
    console.log(`[Cocoon] Track URL: ${trackUrl}`);
    console.log(`[Cocoon] Track blob cached before download: ${!!trackBlobUrl}`);

    if (!trackBlobUrl) {
      console.log(`[Cocoon] Selected track blob not ready, downloading now: ${track.id}`);
      trackBlobUrl = await getTrackBlobUrl(track.id, trackUrl);
    }

    console.log(`[Cocoon] Track blob ready for playback: ${!!trackBlobUrl}`);

    setTrackLabel(isFr ? track.labelFr : track.labelEn);
    currentTrackIdRef.current = track.id;
    setPhase('playing');

    // ── Create Audio element ──
    console.log('[Cocoon] → BEFORE new Audio()');
    const audio = new Audio();
    audio.crossOrigin = 'anonymous';
    audio.preload = 'auto';

    console.log('[Cocoon] crossOrigin=', audio.crossOrigin);
    audioRef.current = audio;
    console.log('[Cocoon] Audio created. Initial state: readyState=', audio.readyState, 'paused=', audio.paused, 'muted=', audio.muted, 'volume=', audio.volume);

    // ── Full event listeners for debug ──
    audio.addEventListener('loadstart', () => console.log('[Cocoon][EVT] loadstart'));
    audio.addEventListener('loadedmetadata', () => console.log(`[Cocoon][EVT] loadedmetadata — duration=${audio.duration}, readyState=${audio.readyState}`));
    audio.addEventListener('loadeddata', () => console.log(`[Cocoon][EVT] loadeddata — readyState=${audio.readyState}`));
    audio.addEventListener('canplay', () => console.log(`[Cocoon][EVT] canplay — readyState=${audio.readyState}, duration=${audio.duration}`));
    audio.addEventListener('canplaythrough', () => console.log(`[Cocoon][EVT] canplaythrough — readyState=${audio.readyState}`));
    audio.addEventListener('play', () => { console.log('[Cocoon][EVT] play — paused=', audio.paused); setIsPlaying(true); });
    audio.addEventListener('playing', () => console.log(`[Cocoon][EVT] playing — currentTime=${audio.currentTime}, volume=${audio.volume}`));
    audio.addEventListener('pause', () => { console.log(`[Cocoon][EVT] pause — currentTime=${audio.currentTime}`); setIsPlaying(false); });
    audio.addEventListener('ended', () => console.log('[Cocoon][EVT] ended'));
    audio.addEventListener('error', () => {
      const e = audio.error;
      console.error(`[Cocoon][EVT] error — code=${e?.code}, message="${e?.message}"`);
      addDebug(`AUD_ERR(${e?.code})`);
    });
    audio.addEventListener('stalled', () => console.warn('[Cocoon][EVT] stalled'));
    audio.addEventListener('suspend', () => console.log('[Cocoon][EVT] suspend'));
    audio.addEventListener('waiting', () => console.log('[Cocoon][EVT] waiting'));
    audio.addEventListener('abort', () => console.warn('[Cocoon][EVT] abort'));
    audio.addEventListener('emptied', () => console.log('[Cocoon][EVT] emptied'));

    // MediaSession
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: isFr ? track.labelFr : track.labelEn,
        artist: 'Hearen', album: 'Cocoon',
      });
      navigator.mediaSession.setActionHandler('play', () => {
        userPausedRef.current = false;
        audioRef.current?.play().catch(() => {});
      });
      navigator.mediaSession.setActionHandler('pause', () => {
        userPausedRef.current = true;
        audioRef.current?.pause();
      });
      navigator.mediaSession.setActionHandler('nexttrack', () => {
        if (autoAdvanceRef.current && audioRef.current) {
          autoAdvanceRef.current(audioRef.current);
        }
      });
    }

    // ── Voice decision ──
    const voiceUrl = voiceUrlRef.current;
    const hasVoice = false; // TEMP TEST Cocoon: bypass voice to validate BG direct playback
    console.log(`[Cocoon] Voice check: preloaded=${!!voiceUrl}, alreadyPlayed=${nightVoicePlayedRef.current}, voiceOn=${voiceOnRef.current} → hasVoice=${hasVoice}`);
    if (voiceUrl) console.log(`[Cocoon] Voice URL: ${voiceUrl}`);

    if (hasVoice) {
      console.log('[Cocoon] ═══ PATH: VOICE → then BG ═══');
      nightVoicePlayedRef.current = true;
      voiceActiveRef.current = true;
      bgTrackUrlRef.current = trackUrl;
      audio.volume = 0.5;
      audio.loop = false;
      audio.src = voiceUrl;
      console.log(`[Cocoon] audio.src SET to voice. State: volume=${audio.volume}, muted=${audio.muted}, readyState=${audio.readyState}, paused=${audio.paused}, currentTime=${audio.currentTime}`);

      // HEAREN V1 — Cocoon long tracks optimization
      // Do NOT fetch the full 30-min BG track as Blob before playback.
      // Let the browser stream the audio directly from R2/API.
      // Blob preload is too slow for long Cocoon files and creates huge startup latency.
      let bgBlobUrl = null;
      console.log('[Cocoon] BG long-track mode: direct streaming URL, no full Blob preload');

      // Override pause handler during voice
      audio.removeEventListener('pause', audio._cocoonPause);
      audio._cocoonPause = () => { if (!voiceActiveRef.current) setIsPlaying(false); };
      audio.addEventListener('pause', audio._cocoonPause);

      audio.addEventListener('ended', function voiceEndHandler() {
        console.log('[Cocoon] Voice ended → switching to BG in 500ms');
        audio.removeEventListener('ended', voiceEndHandler);
        setTimeout(() => {
          voiceActiveRef.current = false;
          audio.volume = volume / 100;
          const bgSrc = bgBlobUrl || bgTrackUrlRef.current;
          console.log(`[Cocoon] BG src: ${bgBlobUrl ? 'blob' : 'direct URL'}, volume=${audio.volume}`);
          const warmup = bgWarmupRef.current;

          if (warmup && warmup.src === bgSrc) {
            console.log('[Cocoon] Reusing warmed BG src');
            audio.src = warmup.src;
          } else {
            console.log('[Cocoon] No warmup available — direct BG src');
            audio.src = bgSrc;
          }
          installBgSafeguards(audio);
          userPausedRef.current = false;
          audio.load();
          console.log('[Cocoon] → BG play() call (after voice)');
          audio.play()
            .then(() => console.log('[Cocoon] ✅ BG play() RESOLVED after voice'))
            .catch(e => console.error(`[Cocoon] ❌ BG play() REJECTED after voice: ${e.name}: ${e.message}`));
          //setTimeout(() => preloadNextTrack(), 3000);
        }, 50);
      });

      console.log('[Cocoon] → CALLING audio.play() for VOICE');
      // HEAREN V1 — Cocoon warm preload
      // Prepare BG stream while the welcome voice is playing.
      // We keep the same <audio> element for iOS safety, but ask the browser
      // to resolve metadata / first chunks as early as possible.
      const warmupAudio = new Audio();
      warmupAudio.preload = 'auto';
      warmupAudio.src = trackUrl;
      warmupAudio.volume = 0;
      warmupAudio.load();

      bgWarmupRef.current = warmupAudio;

      console.log('[Cocoon] BG warmup started during voice');
      
      audio.play().then(() => {
        console.log('[Cocoon] ✅ BG direct play() RESOLVED');
        addDebug('BG_OK');
        audioSessionManager.setPlaying('night', true, 'bg_start');

        const currentId = currentTrackIdRef.current;
        const currentIndex = trackPool.findIndex(t => t.id === currentId);
        const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % trackPool.length : 0;
        const next = trackPool[nextIndex];

        if (next) {
          const nextUrl = `${API_BASE_URL}/api/r2/stream/${next.r2cat}/${next.file}`;
          console.log(`[Cocoon] Preloading NEXT blob immediately: ${next.id}`);
          nextPreparedTrackRef.current = next;
          getTrackBlobUrl(next.id, nextUrl);
        }
      }).catch(err => {
        console.error(`[Cocoon] ❌ Voice play() REJECTED: ${err.name}: ${err.message}`);
        addDebug(`VOICE_FAIL:${err.message?.substring(0, 40)}`);
        voiceActiveRef.current = false;
        audio.volume = volume / 100;
        audio.src = trackBlobUrl || trackUrl;
        console.log(`[Cocoon] Fallback → trying BG play() (${trackBlobUrl ? 'BLOB' : 'URL'})`);
        console.log(`[Cocoon] Fallback state: src=${trackUrl}, volume=${audio.volume}, readyState=${audio.readyState}`);
        installBgSafeguards(audio);
        userPausedRef.current = false;
        audio.load();
        audio.play()
          .then(() => { console.log('[Cocoon] ✅ BG fallback play() RESOLVED'); audioSessionManager.setPlaying('night', true, 'bg_fallback'); })
          .catch(e2 => console.error(`[Cocoon] ❌ BG fallback ALSO REJECTED: ${e2.name}: ${e2.message}`));
        addDebug('BG_FALLBACK');
      });
    } else {
      console.log('[Cocoon] ═══ PATH: BG DIRECT (no voice) ═══');
      addDebug(nightVoicePlayedRef.current ? 'VOICE_DONE' : 'NO_VOICE');
      audio.volume = volume / 100;
      const bgSrc = trackBlobUrl || trackUrl;

      console.log('[Cocoon] FINAL BG SRC =', bgSrc);

      audio.src = bgSrc;

      console.log(`[Cocoon] audio.src SET + load() to BG (${trackBlobUrl ? 'BLOB' : 'URL'}). State: volume=${audio.volume}, muted=${audio.muted}, readyState=${audio.readyState}, paused=${audio.paused}, currentTime=${audio.currentTime}`);

      //installBgSafeguards(audio);
      userPausedRef.current = false;

      setTimeout(() => {
        console.log('[Cocoon] → CALLING audio.play() for BG after load()');
        audio.play().then(() => {
          console.log('[Cocoon] ✅ BG direct play() RESOLVED');
          addDebug('BG_OK');
          audioSessionManager.setPlaying('night', true, 'bg_start');
        }).catch(err => {
          console.error(`[Cocoon] ❌ BG direct play() REJECTED: ${err.name}: ${err.message}`);
          addDebug(`BG_FAIL:${err.message?.substring(0, 40)}`);
        });
      }, 100);
      }
    console.log(`[Cocoon] handleEnter complete — phase=playing, hasVoice=${hasVoice}`);

    // Session-based upsell
    if (!isPremium && getSessionCount() >= UPSELL_THRESHOLD) {
      const msgs = isFr
        ? ['Certaines nuits appellent autre chose…', 'D\'autres ambiances existent, juste pour toi…']
        : ['Some nights call for something else…', 'Other ambiances exist, just for you…'];
      setTimeout(() => setUpsellMsg(msgs[getSessionCount() % msgs.length]), 8000);
    }
  }, [volume, isFr, isPremium, trackPool, preloadNextTrack, currentLang, addDebug, installBgSafeguards]);

  // ─── NEXT: use pre-loaded audio, crossfade ───
  const handleNext = useCallback(async () => {
  if (isTransitioning || !audioRef.current) return;

  setIsTransitioning(true);
  setUpsellMsg(null);

  const audio = audioRef.current;
  const targetVol = volume / 100;

  const next = pickNextTrack(trackPool, playedHistoryRef.current);
  const nextUrl = `${API_BASE_URL}/api/r2/stream/${next.r2cat}/${next.file}`;

  console.log(`[Cocoon] Manual Next selected: ${next.id}`);

  let nextBlobUrl = trackBlobCacheRef.current[next.id] || null;

  if (!nextBlobUrl) {
    console.log(`[Cocoon] Manual Next blob not ready, downloading now: ${next.id}`);
    nextBlobUrl = await getTrackBlobUrl(next.id, nextUrl);
  }

  if (!nextBlobUrl) {
    console.error(`[Cocoon] Manual Next failed: no blob available for ${next.id}`);
    setIsTransitioning(false);
    return;
  }

  playedHistoryRef.current = [...playedHistoryRef.current.slice(-(trackPool.length)), next.id];
  currentTrackIdRef.current = next.id;
  setTrackLabel(isFr ? next.labelFr : next.labelEn);

  // Fade out current track
  const steps = 20;
  const interval = 1500 / steps;

  for (let i = steps; i >= 0; i--) {
    audio.volume = targetVol * (i / steps);
    await new Promise(r => setTimeout(r, interval));
  }

  audio.pause();
  audio.removeAttribute('src');
  audio.load();

  audio.loop = false;
  audio.preload = 'auto';
  audio.src = nextBlobUrl;
  audio.volume = 0;

  userPausedRef.current = false;

  audio.load();

  try {
    await audio.play();
    console.log(`[Cocoon] ✅ Manual Next blob play OK: ${next.id}`);
  } catch (e) {
    console.error(`[Cocoon] ❌ Manual Next blob play KO: ${e.name}: ${e.message}`);
    setIsTransitioning(false);
    return;
  }

  // Fade in next track
  for (let i = 0; i <= steps; i++) {
    if (!audioRef.current) break;
    audioRef.current.volume = targetVol * (i / steps);
    await new Promise(r => setTimeout(r, interval));
  }

  setIsTransitioning(false);
  maybeShowUpsell();

  // Prepare the following track for automatic transition
  const following = pickNextTrack(trackPool, playedHistoryRef.current);
  if (following) {
    const followingUrl = `${API_BASE_URL}/api/r2/stream/${following.r2cat}/${following.file}`;
    console.log(`[Cocoon] Preloading NEXT after manual Next: ${following.id}`);
    nextPreparedTrackRef.current = following;
    getTrackBlobUrl(following.id, followingUrl);
  }
}, [isTransitioning, volume, isFr, trackPool, maybeShowUpsell, getTrackBlobUrl]);

  const handleCenterTap = useCallback(() => {
    // Don't allow pause during voice message
    if (voiceActiveRef.current) return;
    if (!audioRef.current) return;
    if (isPlaying) {
      userPausedRef.current = true;
      audioRef.current.pause();
    } else {
      userPausedRef.current = false;
      audioRef.current.play().catch(() => {});
    }
  }, [isPlaying]);

  const handleExit = useCallback(() => {
    console.log(`[Cocoon] ▶ handleExit CALLED — phase=${phase}, isPlaying=${audioRef.current ? !audioRef.current.paused : 'no-audio'}, currentTime=${audioRef.current?.currentTime ?? 'N/A'}`);
    if (audioRef.current && audioRef.current.currentTime < 1) {
      console.warn('[Cocoon] ⚠ handleExit called with currentTime < 1s — possible early exit!');
    }
    userPausedRef.current = true;
    if (watchdogRef.current) { clearInterval(watchdogRef.current); watchdogRef.current = null; }
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; audioRef.current = null; }
    audioSessionManager.setPlaying('night', false, 'exit');
    audioSessionManager.releaseSession('night');
    navigate(-1);
  }, [navigate, phase]);

  // ─── ENTRY SCREEN ──────────────────────────────────────────────
  if (phase === 'entry') {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center" style={{ background: '#030014' }}>
        <div className="absolute inset-0 pointer-events-none" style={{
          background: `
            radial-gradient(ellipse at 50% 40%, rgba(30, 27, 75, 0.5) 0%, transparent 60%),
            radial-gradient(ellipse at 50% 80%, rgba(17, 24, 39, 0.6) 0%, transparent 50%)
          `,
        }} />
        <button
          onClick={() => navigate(-1)}
          className="absolute top-14 left-5 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 border border-white/15 z-10"
          data-testid="night-back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-white/70" />
        </button>
        <div className="relative z-10 flex flex-col items-center text-center px-8">
          <div className="relative mb-12">
            <div className="absolute inset-0 rounded-full" style={{
              background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)',
              transform: 'scale(3)',
            }} />
            <CocoonIcon size={64} className="text-indigo-300 relative" animate />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">{t?.nightMode}</h1>
          <p className="text-white/70 text-base max-w-[280px] mb-8" style={{ lineHeight: '1.55' }}>
            {isFr
              ? <>Quand le silence devient trop présent...</>
              : <>{`When silence feels too present...`}</>}
          </p>

          {/* Category selector — premium only */}
          {isPremium && (
            <div className="mb-10" data-testid="night-category-selector">
              <p className="text-white/30 text-[11px] uppercase tracking-widest mb-3">
                {isFr ? 'Ambiance de départ' : "Tonight's sound"}
              </p>
              <div className="flex gap-2">
                {NIGHT_CATEGORIES.map(cat => {
                  const active = selectedCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.id)}
                      data-testid={`night-cat-${cat.id}`}
                      className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 ${
                        active
                          ? 'bg-indigo-500/25 text-indigo-200 border border-indigo-400/40'
                          : 'bg-white/[0.04] text-white/35 border border-white/[0.08] active:scale-95'
                      }`}
                    >
                      {isFr ? cat.labelFr : cat.labelEn}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <button
            onClick={handleEnter}
            data-testid="night-enter-btn"
            className="group relative px-10 py-4 rounded-full transition-all duration-200 active:scale-[0.97] hover:scale-[1.02]"
            style={{
              background: 'linear-gradient(135deg, rgba(79,70,229,0.7), rgba(99,102,241,0.5))',
              border: '1.5px solid rgba(129,140,248,0.4)',
              boxShadow: '0 0 40px rgba(99,102,241,0.25), 0 0 80px rgba(99,102,241,0.1), inset 0 1px 0 rgba(165,180,252,0.15)',
            }}
          >
            <span className="text-indigo-100 font-semibold text-base tracking-wide inline-flex items-center justify-center">
              {isEnteringCocoon && (
               <span className="inline-block w-3.5 h-3.5 mr-2 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />
              )}

              {isEnteringCocoon
                ? (isFr ? 'Préparation du refuge...' : 'Preparing your refuge...')
                : t?.nightModeEnter}
            </span>
          </button>
        </div>
      </div>
    );
  }

  // ─── PLAYING SCREEN ────────────────────────────────────────────
  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden" style={{ background: '#030014' }}>

      {/* Discover overlay */}
      {showDiscover && <DiscoverOverlay isFr={isFr} onClose={() => setShowDiscover(false)} />}

      {/* Ambient backgrounds */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: `
          radial-gradient(ellipse at 50% 30%, rgba(30, 27, 75, 0.35) 0%, transparent 55%),
          radial-gradient(ellipse at 30% 70%, rgba(17, 24, 50, 0.4) 0%, transparent 45%),
          radial-gradient(ellipse at 70% 60%, rgba(20, 15, 40, 0.35) 0%, transparent 50%)
        `,
        animation: 'nightDriftBg 45s ease-in-out infinite alternate',
        opacity: fadeIn ? 1 : 0, transition: 'opacity 3s ease',
      }} />
      <div className="absolute inset-0 pointer-events-none" style={{
        background: `
          radial-gradient(ellipse at 60% 25%, rgba(49, 46, 129, 0.12) 0%, transparent 50%),
          radial-gradient(ellipse at 40% 75%, rgba(30, 27, 75, 0.1) 0%, transparent 45%)
        `,
        animation: 'nightDriftBg2 60s ease-in-out infinite alternate',
        opacity: fadeIn ? 0.7 : 0, transition: 'opacity 4s ease 0.5s',
      }} />

      {/* Breathing halos */}
      <div className="absolute pointer-events-none" style={{
        top: '42%', left: '50%', width: '400px', height: '400px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.06) 0%, rgba(99,102,241,0.02) 40%, transparent 70%)',
        animation: isPlaying ? 'nightHaloOuter 10s ease-in-out infinite' : 'none',
        opacity: fadeIn && isPlaying ? 1 : 0, transition: 'opacity 3s ease',
      }} />
      <div className="absolute pointer-events-none" style={{
        top: '42%', left: '50%', width: '260px', height: '260px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, rgba(129,140,248,0.03) 50%, transparent 70%)',
        animation: isPlaying ? 'nightHaloMid 8s ease-in-out infinite' : 'none',
        opacity: fadeIn && isPlaying ? 1 : 0, transition: 'opacity 2.5s ease 0.3s',
      }} />
      <div className="absolute pointer-events-none" style={{
        top: '42%', left: '50%', width: '160px', height: '160px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(129,140,248,0.1) 0%, rgba(99,102,241,0.04) 50%, transparent 70%)',
        animation: isPlaying ? 'nightHaloInner 6s ease-in-out infinite' : 'none',
        opacity: fadeIn && isPlaying ? 1 : 0, transition: 'opacity 2s ease 0.5s',
      }} />

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-5 pt-14 pb-4"
        style={{ opacity: fadeIn ? 1 : 0, transition: 'opacity 1.5s ease 0.3s' }}>
        <button onClick={handleExit}
          className="w-10 h-10 rounded-full flex items-center justify-center bg-white/10 border border-white/15"
          data-testid="night-exit-btn">
          <ArrowLeft className="w-5 h-5 text-white/70" />
        </button>
        <div className="flex items-center gap-2">
          <CocoonIcon size={16} className="text-indigo-400/80" />
          <span className="text-white/50 text-xs font-medium tracking-wider uppercase">{t?.nightMode}</span>
        </div>
        <div className="w-10" />
      </div>

      {/* Center content */}
      <div className="flex-1 flex flex-col items-center justify-center relative z-10 px-8"
        style={{ opacity: fadeIn ? 1 : 0, transition: 'opacity 2s ease 0.6s' }}>

        {/* Play/pause button */}
        <div className="relative mb-6">
          {isPlaying && !isTransitioning && (
            <div className="absolute inset-0 rounded-full" style={{
              animation: 'nightRingBreathe 7s ease-in-out infinite',
              border: '1px solid rgba(129,140,248,0.15)', transform: 'scale(1.5)',
            }} />
          )}
          <button onClick={handleCenterTap} data-testid="night-toggle-play"
            className="relative w-24 h-24 rounded-full flex items-center justify-center transition-all duration-700 active:scale-90"
            style={{
              background: isPlaying ? 'rgba(99,102,241,0.1)' : 'linear-gradient(135deg, rgba(99,102,241,0.3), rgba(79,70,229,0.45))',
              border: `1.5px solid rgba(129,140,248,${isPlaying ? '0.2' : '0.4'})`,
              boxShadow: isPlaying ? '0 0 50px rgba(99,102,241,0.1)' : '0 0 50px rgba(99,102,241,0.2), 0 0 100px rgba(99,102,241,0.08)',
            }}>
            {isPlaying ? <Pause className="w-9 h-9 text-indigo-200/80" /> : <Power className="w-9 h-9 text-indigo-200" />}
          </button>
        </div>

        {/* Track label */}
        <p className={`text-white/50 text-sm font-medium tracking-wide mb-1 transition-opacity duration-700 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
          {trackLabel}
        </p>
        <p className="text-white/35 text-xs mb-4">
          {isTransitioning ? t?.nightModeTransition : isPlaying ? t?.nightModePlaying : t?.nightModePaused}
        </p>

        {/* Next button */}
        {isPlaying && !isTransitioning && (
          <button onClick={handleNext} data-testid="night-next-btn"
            className="flex items-center gap-1 px-4 py-2 rounded-full transition-all duration-500 active:scale-90 mb-4"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <span className="text-white/35 text-[11px] tracking-wide">{t?.nightModeNext}</span>
            <ChevronRight className="w-3.5 h-3.5 text-white/35" />
          </button>
        )}

        {/* Subtle upsell message (inline, no popup) */}
        {upsellMsg && !isPremium && (
          <div
            className="max-w-[260px] text-center animate-fade-in"
            style={{ animation: 'nightFadeIn 1.5s ease forwards' }}
          >
            <p className="text-indigo-300/40 text-xs italic leading-relaxed mb-2">{upsellMsg}</p>
            <button
              onClick={() => setShowDiscover(true)}
              data-testid="night-upsell-discover"
              className="text-indigo-300/50 text-[11px] font-medium tracking-wide hover:text-indigo-300/70 transition-colors"
            >
              {isFr ? 'Explorer' : 'Explore'} →
            </button>
          </div>
        )}
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes nightDriftBg {
          0%   { transform: translate(0, 0) scale(1); }
          100% { transform: translate(3%, -2%) scale(1.04); }
        }
        @keyframes nightDriftBg2 {
          0%   { transform: translate(0, 0) scale(1.02); }
          100% { transform: translate(-2%, 3%) scale(1); }
        }
        @keyframes nightHaloOuter {
          0%, 100% { transform: translate(-50%, -50%) scale(1);   opacity: 0.4; }
          50%      { transform: translate(-50%, -50%) scale(1.18); opacity: 1; }
        }
        @keyframes nightHaloMid {
          0%, 100% { transform: translate(-50%, -50%) scale(1);   opacity: 0.5; }
          50%      { transform: translate(-50%, -50%) scale(1.22); opacity: 1; }
        }
        @keyframes nightHaloInner {
          0%, 100% { transform: translate(-50%, -50%) scale(1);   opacity: 0.5; }
          50%      { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
        }
        @keyframes nightRingBreathe {
          0%, 100% { transform: scale(1.4); opacity: 0.3; }
          50%      { transform: scale(1.7); opacity: 0.7; }
        }
        @keyframes nightFadeIn {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default NightMode;
